/**
 * services/realtime.js — Supabase Realtime subscription bound to stores.
 *
 * Single subscription per account. New messages flow directly into the
 * messages store, status updates patch existing messages, system events
 * update the chats store. No manual DOM manipulation, no module-to-module
 * calls — just store updates that trigger reactive re-renders.
 */

import { createClient } from '@supabase/supabase-js';

import {
  appendMessage,
  patchMessage,
  getMessages,
  addReaction,
} from '../stores/messages.js';
import { bumpChat, updateChat, updateChatByContactId, $chats, $phoneIndex } from '../stores/chats.js';
import { $openChatPhone, $openChatId, $openChatKey, $openChatChannel, openThread, showToast, isChatMuted, isInQuietHours } from '../stores/ui.js';
import { $session } from '../stores/session.js';
import { BROADCAST_EVENTS, DIRECTION, MESSAGE_STATUS, EVENT_TYPES } from '../shared/constants.js';
import { findPendingOutboundMatch } from './realtime-helpers.js';
import { hapticNotify } from './haptics.js';
import * as api from './api.js';
import { loadChats } from './chats-service.js';
import { loadMessages } from './messages-service.js';
import { loadScheduled, removeScheduledById, patchScheduledById } from '../stores/scheduled.js';

let _client = null;
let _channel = null;
let _accountId = null;
let _notifAudio = null;
// S13: guarded read — iOS private mode historically threw on localStorage
// access. The rest of this file already catches setItem; mirror it here
// so a module-load throw can't take out realtime entirely.
let _muted = (() => {
  try { return localStorage.getItem('inbox_v2_sound_muted') === 'true'; }
  catch { return false; }
})();
let _lastSubscribeStatus = null;
// #107 — Supabase Realtime Authorization. Refresh the JWT a few minutes
// before its 1h expiry so the WebSocket never gets disconnected for a
// stale token. Cleared on disconnectRealtime.
let _rtJwtRefreshTimer = null;
const _RT_JWT_REFRESH_MS = 50 * 60 * 1000; // 50 min (token expires at 60 min)

// Reconnect orchestration — single source of truth so we never accumulate
// parallel setTimeouts when multiple CLOSED events fire (which is what
// caused the infinite cascade on 2026-04-13).
let _connecting = false;        // true while a connect attempt is in flight
let _reconnectHandle = null;    // setTimeout handle for the next scheduled retry
let _reconnectDelay = 1500;     // current backoff (ms), grows on each failure
let _wasDisconnected = false;   // #207 — tracks whether we're recovering from a disconnect
// S18 — noise reduction. Track when the current disconnect episode started
// so we only log loudly (a) once at the start of the episode, and (b) if
// we're still flapping after PROLONGED_THRESHOLD_MS. Routine retries go to
// console.debug so the mobile-network-flake cascade stops spamming the
// console on every 1500ms retry.
let _disconnectStartedAt = 0;
let _prolongedLogged     = false;
const _PROLONGED_THRESHOLD_MS = 30_000;
const _RECONNECT_MIN = 1500;
const _RECONNECT_MAX = 30000;
let _channelEpoch = 0;          // bumped on each new channel; subscribe callbacks
                                // capture the epoch and become no-ops if stale

// Debounced chat-list reload. Triggered by chat.new broadcasts AND by
// message_insert for a phone we don't yet know about. Coalesced so a burst
// of events doesn't fire N full /chats fetches.
let _chatListReloadTimer = null;
const _CHAT_LIST_RELOAD_DEBOUNCE = 400;

function _scheduleChatListReload(reason) {
  if (_chatListReloadTimer) return; // already scheduled
  _chatListReloadTimer = setTimeout(() => {
    _chatListReloadTimer = null;
    loadChats({}).catch(e =>
      console.warn('[v2 realtime] chat list reload failed:', e.message)
    );
  }, _CHAT_LIST_RELOAD_DEBOUNCE);
}

function _cancelPendingReconnect() {
  if (_reconnectHandle) {
    clearTimeout(_reconnectHandle);
    _reconnectHandle = null;
  }
}

function _cancelRealtimeJwtRefresh() {
  if (_rtJwtRefreshTimer) {
    clearTimeout(_rtJwtRefreshTimer);
    _rtJwtRefreshTimer = null;
  }
}

// #107 — Refresh the Supabase Realtime JWT before its 1h expiry.
// Realtime drops the WebSocket if a stale token is in use; calling
// setAuth with a fresh token tells the broker to re-evaluate the RLS
// policies for the current connection without re-subscribing.
function _scheduleRealtimeJwtRefresh() {
  _cancelRealtimeJwtRefresh();
  _rtJwtRefreshTimer = setTimeout(async () => {
    _rtJwtRefreshTimer = null;
    if (!_client || !_accountId) return;
    try {
      const cfg = await api.fetchRealtimeConfig();
      if (cfg?.realtimeJwt) {
        _client.realtime.setAuth(cfg.realtimeJwt);
        _scheduleRealtimeJwtRefresh();
      }
    } catch (e) {
      console.warn('[v2 realtime] JWT refresh failed:', e.message);
      // Try again sooner on failure
      _rtJwtRefreshTimer = setTimeout(_scheduleRealtimeJwtRefresh, 60_000);
    }
  }, _RT_JWT_REFRESH_MS);
}

function _scheduleReconnect() {
  _cancelPendingReconnect();
  // Don't reconnect if the tab is hidden — visibilitychange will trigger
  // the right reconnect when the user returns. Avoids burning battery and
  // hammering the server while backgrounded.
  if (typeof document !== 'undefined' && document.hidden) {
    return;
  }

  // S18 — noise-reduced logging.
  //   - First schedule of a disconnect episode: console.info once.
  //   - Routine subsequent retries: console.debug (filtered out of most
  //     default console views — keeps mobile-network-flake cascades quiet).
  //   - When still flapping past PROLONGED_THRESHOLD_MS: console.warn once
  //     so genuinely broken realtime surfaces to anyone watching the console.
  const now = Date.now();
  if (_disconnectStartedAt === 0) {
    _disconnectStartedAt = now;
    _prolongedLogged = false;
    console.info('[v2 realtime] disconnected — will retry in', _reconnectDelay, 'ms');
  } else if (!_prolongedLogged && (now - _disconnectStartedAt) > _PROLONGED_THRESHOLD_MS) {
    _prolongedLogged = true;
    console.warn('[v2 realtime] still disconnected after',
      Math.round((now - _disconnectStartedAt) / 1000), 's — continuing retries');
  } else {
    console.debug('[v2 realtime] scheduling reconnect in', _reconnectDelay, 'ms');
  }

  _reconnectHandle = setTimeout(() => {
    _reconnectHandle = null;
    if (!_accountId) return;
    // Exponential backoff up to the cap
    _reconnectDelay = Math.min(_reconnectDelay * 2, _RECONNECT_MAX);
    connectRealtime(_accountId, { force: true });
  }, _reconnectDelay);
}

// ── Sound ────────────────────────────────────────────────────────────────────

function _playSound() {
  if (_muted) return;
  if (!_notifAudio) _notifAudio = new Audio('/inbox/assets/sounds/notification.mp3');
  _notifAudio.currentTime = 0;
  _notifAudio.play().catch(() => {});
  // Pair with a short vibrate on supporting devices (Android Chrome/Edge).
  // Mute state is read inside hapticNotify itself so this stays in sync
  // with the user's silence preference.
  hapticNotify();
}

export function toggleSound() {
  _muted = !_muted;
  localStorage.setItem('inbox_v2_sound_muted', String(_muted));
  return !_muted;
}

export function isSoundOn() { return !_muted; }

// ── Connect ──────────────────────────────────────────────────────────────────

export async function connectRealtime(accountId, { force = false } = {}) {

  // Already have a LIVE subscription to this account → cheap skip.
  if (!force && _accountId === accountId && _channel && _lastSubscribeStatus === 'SUBSCRIBED') {
    return;
  }

  // Re-entrancy guard. Multiple call sites (init, $session listener,
  // visibilitychange, CLOSED auto-reconnect) can fire connectRealtime in
  // parallel for the same account. Without this guard, each tears down the
  // others mid-handshake, emitting "WebSocket is closed before the
  // connection is established" cascades. force-from-CLOSED reconnects also
  // respect the guard — they'll be picked up on the next retry tick.
  if (_connecting) {
    return;
  }
  _connecting = true;

  // Cancel any pending reconnect timer — we're about to do the work it would.
  _cancelPendingReconnect();

  _accountId = accountId;

  // Get Supabase config from our auth function
  let cfg;
  try {
    cfg = await api.fetchRealtimeConfig();
  } catch (e) {
    console.warn('[v2 realtime] config fetch failed:', e.message);
    _connecting = false;
    _scheduleReconnect();
    return;
  }
  if (!cfg?.supabaseUrl || !cfg?.anonKey) {
    console.warn('[v2 realtime] missing config');
    _connecting = false;
    return;
  }

  // Create client once
  if (!_client) {
    _client = createClient(cfg.supabaseUrl, cfg.anonKey, {
      auth: { persistSession: false, autoRefreshToken: false },
      realtime: {
        // #206 — Web Worker heartbeat. Offloads the 25-second heartbeat
        // ping to a Web Worker thread that isn't throttled when the
        // browser backgrounds the tab/PWA. Without this, iOS kills the
        // heartbeat timer → server drops the connection → messages stop.
        worker: true,
        // #206 — heartbeatCallback. Fires when the connection health
        // changes. On 'disconnected' we schedule a reconnect.
        //
        // S10: do NOT mutate _connecting from here — the subscribe
        // callback owns that flag. If a connect attempt is genuinely
        // in flight when the heartbeat reports disconnected, clobbering
        // _connecting=false from the heartbeat lets a second connect
        // start in parallel; both then race through the _channelEpoch
        // guard and one ends up as the ghost. Just flag
        // _wasDisconnected + ask for a reconnect; the existing connect
        // re-entrancy guard (_connecting) + epoch stale-callback guard
        // handle the rest. Matches the pattern CHANNEL_ERROR /
        // TIMED_OUT / CLOSED in the subscribe callback follow, so
        // "heartbeat says disconnected" and "broker says CLOSED" now
        // flow through the same reconcile path.
        heartbeatCallback: (status) => {
          if (status === 'disconnected' && _accountId) {
            _wasDisconnected = true;
            _scheduleReconnect();
          }
        },
      },
    });
  }

  // #107 v2 — Realtime Authorization, runtime-flag gated.
  //
  // The server returns realtimeAuthEnabled:true ONLY when both:
  //   (a) the INBOX_RT_AUTH_ENABLED env var is set, AND
  //   (b) the SUPABASE_JWT_SECRET-signed realtimeJwt was minted successfully
  //
  // When enabled, we:
  //   1. Pass the JWT to setAuth() so the broker's RLS policy can match
  //      our account_id claim against the topic name
  //   2. Subscribe to the AUTHORIZED topic (`inbox:<accountId>`) with
  //      private:true
  //   3. Schedule a JWT refresh before the 1h expiry so the WebSocket
  //      doesn't get dropped for staleness
  //
  // When disabled (default), we stay on the legacy HMAC topic — same
  // path that's been running since #81. Failures of the new path are
  // logged but don't block the connect; we fall back to legacy on the
  // next reconnect cycle.
  const useAuthorizedPath = Boolean(cfg.realtimeAuthEnabled && cfg.realtimeJwt);
  if (useAuthorizedPath) {
    try {
      _client.realtime.setAuth(cfg.realtimeJwt);
      _scheduleRealtimeJwtRefresh();
    } catch (e) {
      console.warn('[v2 realtime] #107 — setAuth failed, falling back to legacy:', e.message);
      // useAuthorizedPath stays true — let the subscribe attempt run, the
      // CHANNEL_ERROR handler below will trip _scheduleReconnect which
      // will refetch the config and try again. If the env flag is the
      // problem, this loop is bounded by the watchdog timer.
    }
  }

  // Tear down old channel if reconnecting. The removeChannel call CAN emit
  // "closed before established" errors in the realtime-js disconnect path
  // when the WS hadn't finished its handshake — those errors are noisy but
  // benign because we're done with the channel anyway. The real protection
  // against cascading CLOSED-handler reconnects is the epoch capture below.
  const previousChannel = _channel;
  _channel = null;
  if (previousChannel) {
    _client.removeChannel(previousChannel).catch(() => {});
  }

  // Bump epoch BEFORE building the new channel so that any in-flight
  // CLOSED callbacks from the old one (which captured the previous epoch)
  // become no-ops when they finally fire.
  _channelEpoch += 1;
  const myEpoch = _channelEpoch;

  // We use BROADCAST channels (not postgres_changes) because the
  // inbox_messages RLS policy uses a SET LOCAL Postgres GUC that the
  // realtime broker can't populate — postgres_changes silently filters
  // every row out for anon clients. Server-side (webhook + proxy)
  // publishes via inbox-realtime.js helper using the service key.
  //
  // #107 v2: pick the topic + private flag based on the runtime flag.
  // Authorized path uses `inbox:<accountId>` + private:true gated by the
  // realtime.messages RLS SELECT policy. Legacy path uses the HMAC-suffixed
  // topic from #81 with private:false (bg compatibility).
  const topic = useAuthorizedPath
    ? (cfg.authorizedBroadcastTopic || cfg.broadcastTopic)
    : (cfg.legacyBroadcastTopic     || cfg.broadcastTopic);
  if (!topic) {
    console.warn('[v2 realtime] config missing broadcastTopic — server out of date or auth failed');
    _connecting = false;
    return;
  }
  _channel = _client
    .channel(topic, {
      config: {
        broadcast: { self: false, ack: false },
        private:   useAuthorizedPath,
      },
    })
    .on('broadcast', { event: BROADCAST_EVENTS.MESSAGE_INSERT }, ({ payload }) => {
      _handleMessageInsert(payload);
    })
    .on('broadcast', { event: BROADCAST_EVENTS.MESSAGE_UPDATE }, ({ payload }) => {
      _handleMessageUpdate(payload);
    })
    .on('broadcast', { event: BROADCAST_EVENTS.EVENT_INSERT }, ({ payload }) => {
      _handleEvent(payload);
    })
    .on('broadcast', { event: BROADCAST_EVENTS.SCHEDULED_UPDATE }, ({ payload }) => {
      _handleScheduledUpdate(payload);
    })
    .subscribe((status, err) => {
      // Stale-callback guard: if a newer channel has been built since this
      // closure was set up, this callback is from a torn-down ghost — drop it.
      // This is the critical fix that breaks the runaway cascade.
      if (myEpoch !== _channelEpoch) {
        // Don't even log — a healthy reconnect will fire many of these.
        return;
      }

      _lastSubscribeStatus = status;

      if (status === 'SUBSCRIBED') {
        _reconnectDelay = _RECONNECT_MIN;
        _connecting = false;
        // S18 — clear the disconnect-episode tracker so the NEXT disconnect
        // gets a fresh "disconnected — will retry" info log. Also surface
        // the recovery if we'd previously escalated to a prolonged warn.
        if (_prolongedLogged) {
          const dur = Math.round((Date.now() - _disconnectStartedAt) / 1000);
          console.info('[v2 realtime] reconnected after', dur, 's');
        }
        _disconnectStartedAt = 0;
        _prolongedLogged = false;

        // #207 — Post-reconnect catch-up. If we're recovering from a
        // prior disconnect, broadcasts during the gap were lost. Refresh
        // the chat list and the currently open thread so missed messages
        // hydrate immediately — same pattern WhatsApp Web uses.
        //
        // S16 — use the EXACT per-channel row via $openChatKey, not a
        // contactId-only first-match. Pre-S16 this was the "Prinstaindia
        // redirects to ODIV" bug: both rows share contactId, so
        // Array.find returned whichever chat had been bumped most
        // recently (sort-by-last_message_ts desc). Post-reconnect
        // loadMessages then called $openChatChannel.set() with the
        // WRONG channel, yanking the user into the other thread.
        if (_wasDisconnected) {
          _wasDisconnected = false;
          loadChats({}).catch(e =>
            console.warn('[v2 realtime] post-reconnect loadChats failed:', e.message)
          );
          const openPhone    = $openChatPhone.get();
          const openChatId   = $openChatId.get();
          const openChannel  = $openChatChannel.get();
          const openKey      = $openChatKey.get();
          if (openPhone && openChatId) {
            // Prefer the composite chat_key lookup — it's the single
            // source of truth for which row the user is actually
            // looking at. Fall back to $openChatChannel if the key
            // lookup misses (loadChats may not have repopulated yet
            // on the reconnect race).
            const chatRow = openKey ? $chats.get()[openKey] : null;
            const ch = chatRow?.channelPhoneId || openChannel || null;
            if (ch) {
              loadMessages(openChatId, openPhone, ch).catch(e =>
                console.warn('[v2 realtime] post-reconnect loadMessages failed:', e.message)
              );
              // S23 — rehydrate the scheduled queue for the open thread so
              // any cron-fired rows that cleared during the gap don't linger
              // as ghost bubbles, and any newly-queued rows surface.
              loadScheduled(openPhone, ch).catch(e =>
                console.warn('[v2 realtime] post-reconnect loadScheduled failed:', e.message)
              );
            }
          }
        }
        return;
      }

      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        _connecting = false;
        _wasDisconnected = true;
        _scheduleReconnect();
      }
    });

  // Belt-and-suspenders: if the subscribe callback never fires (e.g. the
  // server hangs the handshake), make sure _connecting eventually clears.
  // We use a 10-second watchdog. Most healthy connects resolve in <1s.
  // S18 — watchdog fire is a debug-level event during flake episodes; only
  // escalates to warn if _scheduleReconnect determines the disconnect has
  // gone prolonged.
  setTimeout(() => {
    if (_connecting && _channelEpoch === myEpoch && _lastSubscribeStatus !== 'SUBSCRIBED') {
      console.debug('[v2 realtime] subscribe watchdog — clearing _connecting');
      _connecting = false;
      _scheduleReconnect();
    }
  }, 10000);
}

export function reconnectRealtime() {
  if (!_accountId) return;
  // Only force-reconnect when the channel is actually dead. If it's
  // already SUBSCRIBED, tearing it down just to rebuild it causes
  // WebSocket churn (visible as cascading CLOSED → CHANNEL_ERROR in
  // the console on every tab switch / Inspector toggle).
  if (_lastSubscribeStatus === 'SUBSCRIBED' && _channel) {
    return;
  }
  connectRealtime(_accountId, { force: true });
}

export async function disconnectRealtime() {
  _cancelPendingReconnect();
  _cancelRealtimeJwtRefresh();
  if (_chatListReloadTimer) {
    clearTimeout(_chatListReloadTimer);
    _chatListReloadTimer = null;
  }
  _channelEpoch += 1; // any stale callbacks become no-ops
  _connecting = false;
  if (_channel && _client) {
    await _client.removeChannel(_channel).catch(() => {});
    _channel = null;
  }
  _lastSubscribeStatus = null;
  _accountId = null;
  _reconnectDelay = _RECONNECT_MIN;
  // S18 — clear disconnect-episode tracker on full disconnect (logout)
  // so a subsequent login doesn't inherit stale "prolonged" state.
  _disconnectStartedAt = 0;
  _prolongedLogged = false;
}

// ── Handlers ─────────────────────────────────────────────────────────────────

// Legacy fallback matcher lives in realtime-helpers.js as
// `findPendingOutboundMatch` so it can be unit-tested in isolation
// (#126). See the JSDoc on that function for strategy details.

function _handleMessageInsert(row) {
  if (!row || !row.chat_id) return;

  // ── Reactions are NOT thread messages — they're metadata on a target ────
  // Route to $reactions store instead of $threads, then return.
  // S5: key reactions by (phone, channel) so they don't cross-contaminate
  // between two channels carrying the same contact.
  if (row.type === 'reaction') {
    const targetWamid =
      row.reply_to_wamid ||
      row.body?.reaction?.message_id ||
      row.body?.context?.message_id ||
      null;
    const emoji = row.body?.reaction?.emoji || '';
    if (!targetWamid) {
      console.warn('[v2 realtime] reaction without target wamid, dropping', row);
      return;
    }
    addReaction(row.chat_id, row.channel_id, targetWamid, {
      emoji,
      fromMe:    row.direction === DIRECTION.OUTBOUND,
      fromPhone: row.from_phone || null,
      timestamp: row.timestamp || Math.floor(Date.now() / 1000),
      wamid:     row.wamid || null,
    });
    return;
  }

  // Normalize Supabase row → store message shape
  const msg = {
    id:        row.wamid || row.id,
    wamid:     row.wamid,
    metaWamid: row.meta_wamid || null,
    clientId:  row.correlation_id || null, // Phase B (#136) — for outbound dedup
    type:      row.type,
    direction: row.direction,
    fromMe:    row.direction === DIRECTION.OUTBOUND,
    status:    row.status,
    timestamp: row.timestamp,
    fromPhone: row.from_phone,
    toPhone:   row.to_phone,
    agentName: row.agent_name,
    replyTo:   row.reply_to_wamid,
    ...(typeof row.body === 'object' ? row.body : {}),
  };

  // Outbound dedup: the broadcast may arrive BEFORE the send-API response
  // has had a chance to patch the optimistic message with its real wamid.
  // Two dedup paths, tried in order:
  //
  //   1. Phase B (#136) — deterministic match by correlation_id. The
  //      client generated the correlation_id BEFORE sending and stored
  //      it as the optimistic row's id (and clientId). The proxy
  //      persisted it on inbox_messages.correlation_id and the broadcast
  //      payload echoes it. O(1) lookup, zero ambiguity.
  //
  //   2. Legacy fallback — content-matching heuristic via
  //      _findPendingOutboundMatch. Only fires when the broadcast row
  //      lacks correlation_id (older client builds in the field that
  //      haven't picked up #136 yet) OR the optimistic row was created
  //      before #136 shipped. Will be removed in a follow-up commit
  //      once the SW rollout is verified at 100%.
  // S23 — if the outbound broadcast's correlation_id matches a
  // scheduled row we're tracking in $scheduled, remove that ghost
  // bubble so the real bubble takes over. Cron stamps
  // correlation_id = scheduled.id after S23.
  if (row.direction === DIRECTION.OUTBOUND && row.correlation_id) {
    removeScheduledById(row.correlation_id);
  }

  if (row.direction === DIRECTION.OUTBOUND) {
    const thread = getMessages(row.chat_id, row.channel_id);
    let pending = null;

    // Strategy 1: correlation_id (Phase B primary path)
    if (row.correlation_id) {
      pending = thread.find(m => m.clientId === row.correlation_id);
      if (pending) {
      }
    }

    // Strategy 2: legacy content-match fallback
    if (!pending && row.wamid) {
      pending = findPendingOutboundMatch(thread, row);
      if (pending) {
      }
    }

    if (pending) {
      patchMessage(row.chat_id, row.channel_id, pending.id, {
        wamid: row.wamid,
        id:    row.wamid || pending.id,
        status: row.status || MESSAGE_STATUS.SENT,
        timestamp: row.timestamp || pending.timestamp,
        // Keep clientId pinned so a second broadcast for the same row
        // (e.g. status update broadcast carrying the same correlation_id
        // alongside the message_insert one) still matches in strategy 1.
        clientId: pending.clientId || row.correlation_id || null,
      });
      // Still propagate chat list metadata so the row bumps + preview update.
      // #153: lookup via composite (phone, channel) so an outbound echo
      // lands on the per-channel row the message was actually sent from.
      const chatRowId = $phoneIndex.get()[`${row.chat_id}:${row.channel_id || ''}`];
      if (chatRowId) {
        const preview = row.preview || (msg.text?.body || `[${msg.type}]`);
        updateChat(chatRowId, { lastMessage: preview, lastSeen: row.timestamp });
        bumpChat(chatRowId);
      }
      return;
    }
  }

  appendMessage(row.chat_id, row.channel_id, msg);

  // #153: route by composite (phone, channel_id). A single contact messaging
  // on two channels has two store rows, and an incoming message must bump
  // exactly one of them without polluting the other's preview/timestamps.
  const chatId = $phoneIndex.get()[`${row.chat_id}:${row.channel_id || ''}`];
  if (chatId) {
    const preview = row.preview || (msg.text?.body || `[${msg.type}]`);
    // Keep the activity timestamps live so the 24h session window guard
    // flips open the moment a customer replies, without waiting for a
    // manual chat list refresh. lastMessageTs bumps on both directions;
    // lastInboundTs only on inbound — this is what Phase 2.4 reads.
    const tsPatch = { lastMessage: preview, lastSeen: row.timestamp };
    if (row.timestamp) tsPatch.lastMessageTs = row.timestamp;
    if (row.direction === DIRECTION.INBOUND && row.timestamp) {
      tsPatch.lastInboundTs = row.timestamp;
      // #209: bump unread unless THIS SPECIFIC per-channel row is the one
      // currently open. Old guard checked phone only — same contact on two
      // channels would block the second channel's unread bump when the
      // first was open. Now checks the composite chat row key.
      if (chatId !== $openChatKey.get()) {
        const existing = $chats.get()[chatId];
        tsPatch.unread = (existing?.unread || 0) + 1;
        // T14 — direct $accountUnread bump removed. The atom is now a
        // `computed` derived from $chats (local sum) + $serverAccountUnread
        // (server floor); updateChat(chatId, {unread: N+1}) below
        // automatically flows through the derived value.
      } else {
        // T11 — inbound landed on the currently-open thread. FCM push
        // for this same message already bumped the OS badge (server
        // computed count++), but locally we correctly skip the
        // chat-row unread bump because the agent is actively viewing
        // the thread. Without reconciliation the two diverge: chat
        // list shows 0, home-screen icon shows 1. Fire markUnreadAsRead
        // so the server recomputes + returns the authoritative count,
        // which the T11 client handler writes back to $accountUnread
        // (and thus clears the OS badge). Fire-and-forget; self-
        // throttles via status filters in the proxy.
        import('./messages-outbound.js').then(mod =>
          mod.markUnreadAsRead(row.chat_id, row.channel_id).catch(() => {})
        );
      }
    }
    updateChat(chatId, tsPatch);
    bumpChat(chatId);
  } else {
    // Unknown (chat_id, channel_id) tuple. Either:
    //   (a) a brand-new contact messaged us for the first time — the CRM is
    //       creating the contact row and the PWA will pick it up on reload, OR
    //   (b) a KNOWN contact messaged us on a NEW channel they'd never used —
    //       the existing row exists only under the old channel, and we need
    //       to create a second row for the new channel.
    // Both cases resolve with a debounced chat list reload — the proxy will
    // fan out the new (contact, channel) pair on the next /chats call.
    _scheduleChatListReload('unknown (chat_id, channel) ' + row.chat_id + ':' + row.channel_id);
  }

  // Sound only if this thread is NOT open + message is inbound.
  // #180 + #181: also suppress when the contact is muted or we're
  // inside the agent's quiet-hours window. Broadcast rows don't always
  // carry contact_id, so resolve it via the chats store the same way
  // the bump path above did.
  if (row.direction === DIRECTION.INBOUND && $openChatPhone.get() !== row.chat_id) {
    if (isInQuietHours()) {
      // global silence — skip
    } else {
      const lookupKey  = $phoneIndex.get()[`${row.chat_id}:${row.channel_id || ''}`];
      const contactId  = lookupKey ? $chats.get()[lookupKey]?.contactId : null;
      if (!isChatMuted(contactId)) _playSound();
    }
  }
}

function _handleMessageUpdate(row) {
  if (!row || !row.chat_id || !row.wamid) return;
  // Always propagate status. Also stamp metaWamid into the local store
  // when it transitions from null → known — needed so reactions from
  // customers (which reference Meta wamid) can resolve to the right bubble.
  const patch = { status: row.status };
  if (row.meta_wamid) patch.metaWamid = row.meta_wamid;
  patchMessage(row.chat_id, row.channel_id, row.wamid, patch);
}

/**
 * T2 — terminal transition on an inbox_scheduled_messages row. The cron
 * emits this on sent / failed / cancelled-by-cron. The outbound
 * message_insert broadcast (S23 path) already removes ghosts on success —
 * this handler is the ONLY realtime signal for the failure path, so
 * ghost bubbles no longer rot in the thread when the cron can't deliver.
 *
 * Behavior:
 *   - failed: stamp { _uiStatus:'failed', _failReason } on the row so
 *     the Thread renders the red "Failed · Retry · Dismiss" state. The
 *     row stays in $scheduled for the user to act on; there's no
 *     auto-remove timer because the whole point is that the user must
 *     see it (a11y: role=alert + aria-live=assertive in the component).
 *   - sent / cancelled: remove immediately. Defensive on sent (S23 covers
 *     the happy path via correlation_id). cancelled is rare — only the
 *     cron fires this, and only for the keep-alive displacement path.
 */
function _handleScheduledUpdate(row) {
  if (!row || !row.id) return;
  if (row.status === 'failed') {
    patchScheduledById(row.id, {
      _uiStatus:  'failed',
      _failReason: row.error_reason || 'unknown',
    });
    return;
  }
  if (row.status === 'sent' || row.status === 'cancelled') {
    removeScheduledById(row.id);
  }
}

function _handleEvent(row) {
  if (!row || !row.event_type) return;

  // A brand-new chat was created on the CRM side (e.g. first-ever inbound from
  // a stranger, or a chat initiated from another device). Reload the chat list
  // debounced so bursts of chat.new + message.incoming coalesce into one fetch.
  if (row.event_type === EVENT_TYPES.CHAT_NEW) {
    _scheduleChatListReload('chat.new event');
    return;
  }

  // chat.assigned — another agent (or this agent on a different device)
  // assigned/unassigned a chat. Patch the chat row's `agent` field so the
  // assignee chip on the chat list updates without a manual reload (#76).
  // The backend ALSO inserts an inbox_messages system row that flows
  // through _handleMessageInsert and becomes a visible divider in the
  // thread — that path handles the in-thread UI; this path handles the
  // chat-list metadata.
  if (row.event_type === EVENT_TYPES.CHAT_ASSIGNED) {
    // #163: per-row assignment. The broadcast payload carries:
    //   - chat_id     — phone (normalized)
    //   - contact_id  — CRM contact ObjectId
    //   - channel_id  — Partner UUID (null when caller used legacy
    //                    contact-scoped /assign route)
    //   - agent_email — new agent (null when cleared or done)
    //   - is_done     — new done flag
    //
    // Two paths:
    //   1. channel_id present → use composite ${phone}:${channel_id}
    //      lookup via $phoneIndex to patch the exact row. The other
    //      channel's row for the same contact stays untouched.
    //   2. channel_id absent → legacy contact-scoped fallback via
    //      updateChatByContactId (iterates $chats by contactId, still
    //      useful when the CRM or an automation fires the old assign
    //      route with no channel context).
    //
    // Note: row.chat_id (phone) in the new broadcast shape wouldn't
    // match updateChatByContactId (which expects contact_id) — that
    // was a latent #153 regression where the listener silently
    // no-oped. Fixed here by reading row.contact_id explicitly in
    // the legacy branch.
    if (row.channel_id && row.chat_id) {
      const key = $phoneIndex.get()[`${row.chat_id}:${row.channel_id}`];
      if (key) {
        updateChat(key, {
          agent:  row.agent_email || null,
          isDone: !!row.is_done,
        });
      }
    } else if (row.contact_id) {
      updateChatByContactId(row.contact_id, {
        agent:  row.agent_email || null,
        isDone: !!row.is_done,
      });
    }
    // #173 — in-app foreground toast when THIS user just got assigned a
    // chat they're not currently viewing. Suppressed for self-claims
    // (assigner === assignee) and for the chat the user is already in.
    _maybeShowAssignmentToast(row);
    return;
  }

  // chat.done / chat.closed — the divider system row already lands in the
  // thread via _handleMessageInsert. The chat list itself doesn't render
  // a "done"/"closed" status today, so there's nothing else to patch on
  // the store side — but log so the broadcast contract is observable.
  if (row.event_type === 'chat.done' || row.event_type === EVENT_TYPES.CHAT_CLOSED) {
    return;
  }
}

/**
 * #173 — In-app foreground toast for "you just got assigned a chat".
 *
 * Fires when this agent is the new assignee on a chat they don't currently
 * have open. Skipped for self-claims (assigner === assignee — they don't
 * need to be notified of their own action) and for the chat that's already
 * on screen (the assignee chip will visibly update in the header).
 *
 * The FCM push handles the same notification when the PWA is backgrounded;
 * this is the foreground equivalent so the agent doesn't need an OS-level
 * banner permission to be informed of new work.
 *
 * Tappable: tapping the toast openThread()s the chat directly. Realtime is
 * usually faster than HTTP refetch, so the row should already exist in the
 * store; but if a race puts the broadcast ahead of the chat list reload,
 * we fall back to a no-op (the toast still shows, click just doesn't open).
 */
function _maybeShowAssignmentToast(row) {
  if (!row || row.event_type !== EVENT_TYPES.CHAT_ASSIGNED) return;
  if (!row.agent_email) return; // unassign / clear — no toast

  const session = $session.get();
  const myEmail = (session?.agentEmail || '').toLowerCase();
  if (!myEmail) return;
  if (String(row.agent_email).toLowerCase() !== myEmail) return; // not for me

  // Self-claim: the assigner IS me. No toast for "I just claimed this chat".
  if ((row.assigner_email || '').toLowerCase() === myEmail) return;

  // Already viewing this chat — the header chip is the right surface.
  if ($openChatId.get() === row.contact_id) return;

  // #180 + #181: respect mute + quiet hours. Assignments still fire the
  // FCM push (server-side), but the in-app surfacing stays quiet.
  if (isChatMuted(row.contact_id)) return;
  if (isInQuietHours()) return;

  const assignerName = row.assigner_name || row.assigner_email || 'Someone';
  const customer     = row.customer_name || 'a chat';
  const note         = row.handoff_note ? String(row.handoff_note).slice(0, 80) : '';
  const message      = note
    ? `${assignerName} assigned you ${customer} — “${note}”`
    : `${assignerName} assigned you ${customer}`;

  // Find the local chat row so the click handler can openThread without
  // refetching. If we can't find one yet (race), just show a plain toast.
  let chatRow = null;
  if (row.contact_id) {
    const all = $chats.get();
    for (const key in all) {
      const c = all[key];
      if (c?.contactId === row.contact_id &&
          (!row.channel_id || c.channelPhoneId === row.channel_id)) {
        chatRow = c;
        break;
      }
    }
  }

  // Audible nudge — same sound as a new inbound message so the agent
  // recognises "you have something to look at".
  _playSound();

  showToast(message, {
    duration: 6000,
    kind:     'assign',
    onClick:  chatRow
      ? () => openThread({
          chatKey:        chatRow.id,
          chatId:         chatRow.contactId,
          phone:          chatRow.phone,
          channelPhoneId: chatRow.channelPhoneId,
        })
      : null,
  });
}
